/**
 * TH-55392: Hook para rastrear visualizações de artes na galeria.
 *
 * Usa IntersectionObserver para disparar POST /custom-arts/{id}/view
 * quando um card de arte entra no viewport. Fire-and-forget (ignora erros).
 *
 * Deduplicação em-memória: Set<art_id> rastreia artes já disparadas
 * nesta sessão. ON CONFLICT do servidor garante idempotência por dia/usuário.
 */

import { useEffect, useRef } from "react";

const STORAGE_KEY = "artViewsTracked";

export function useArtViewTracker(artId: string | null, enabled = true) {
  const trackedRef = useRef<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!enabled || !artId) return;

    // Restaurar set de views rastreadas da sessão
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        trackedRef.current = new Set(JSON.parse(stored));
      } catch {
        trackedRef.current = new Set();
      }
    }

    // Callback de intersecção
    const handleIntersection = async (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.isIntersecting && artId && !trackedRef.current.has(artId)) {
          // Disparar view — fire-and-forget
          // PSHWO-64518: era `/api/v1/custom-arts/...` — prefixo `v1` não
          // existe na API (router monta em `/custom-arts`) e o POST caía em
          // 404 silencioso (fire-and-forget), então view_count nunca subia.
          fetch(`/api/custom-arts/${artId}/view`, {
            method: "POST",
            credentials: "include",
          }).catch(() => {
            // Ignorar erros de rede
          });

          // Marcar como rastreado nesta sessão
          trackedRef.current.add(artId);
          sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify(Array.from(trackedRef.current))
          );
        }
      }
    };

    // Criar observer
    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold: 0.1, // Ativa quando 10% do elemento é visível
    });

    // Attach ao elemento (via ref do componente pai)
    // NOTA: componente pai precisa passar ref={elementRef}
    // Aqui registramos a função globalmente; componente CustomArtsPage
    // chama useArtViewTracker(artId) pra cada card e passa a ref.
  }, [artId, enabled]);

  return {
    observerRef,
    markAsViewed: (id: string) => {
      trackedRef.current.add(id);
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(trackedRef.current))
      );
    },
  };
}

/**
 * Helper para conectar observer a um elemento.
 * Uso: const ref = useArtViewTrackerElement(artId, enabled);
 */
export function useArtViewTrackerElement(artId: string | null, enabled = true) {
  const { observerRef } = useArtViewTracker(artId, enabled);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!observerRef.current || !elementRef.current) return;
    observerRef.current.observe(elementRef.current);
    return () => {
      if (elementRef.current) {
        observerRef.current?.unobserve(elementRef.current);
      }
    };
  }, [observerRef]);

  return elementRef;
}
